<template>
  <v-select
    :chips="field.multiple"
    :clearable="field.clearable"
    :disabled="field.readonly"
    :error-messages="errors"
    :hide-details="dense"
    item-title="text"
    item-value="value"
    :items="allOptions"
    menu-icon="$expand"
    :model-value="itemValue()"
    :multiple="field.multiple"
    :rules="rules"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #label>
      <FieldLabel :field="field" />
    </template>

    <template
      v-if="field.optionForm && field.optionForm.createFunc"
      #prepend-item
    >
      <v-list-item class="pa-0">
        <v-btn
          class="ml-4 mb-4"
          color="primary"
          @click="openCreateForm()"
        >
          <template #prepend>
            <vi-icon icon="mdi:plus" />
          </template>
          {{ field.optionForm.title }}
        </v-btn>
      </v-list-item>

      <v-divider />
    </template>
  </v-select>
</template>

<script setup lang="ts">
const props = defineProps<{
  field: FieldConfig
  rules?: FormRule[]
  errors?: string[]
  dense?: boolean
  modelValue: GenericData
}>();
const emit = defineEmits(['update:modelValue']);
const createdOptions = ref<ChoiceOption[]>([]);
const fieldRef = toRef(props, 'field');
const allOptions = computed(() => [...(fieldRef.value.options || []), ...createdOptions.value]);
const toastAlerts = useToastAlerts();

/**
 * Usually we will be recieving the value directly from the JSON, like for hard-coded pick-lists or
 * when the BE serializer sends us an ID value. But sometimes we will receive a full reference object,
 * and in these cases the select-list will need value the object.id in order to show the current selection.
 */
const itemValue = () => {
  if (props.modelValue !== null && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
    if (typeof props.modelValue[0] === 'object') {
      return (props.modelValue as any[]).map(x => x.id);
    }
    else {
      return props.modelValue;
    }
  }
  else if (props.modelValue !== null && typeof props.modelValue === 'object') {
    return (props.modelValue as any).id;
  }
  else {
    return props.modelValue;
  };
};

const { openFormDialog } = useFormDialogStore();

/**
 * Opens a form dialog configured with the fields from the `field.optionForm`
 * config object. When the form is successfully submitted, it appends a new option the
 * list of options and selects it.
 */
const openCreateForm = () => {
  const config = props.field.optionForm!;
  openFormDialog(config.title, { fields: config.fields }, {
    onSubmit: async data => {
      if (config.createFunc) {
        try {
          const newOption = await config.createFunc(data);
          createdOptions.value.push(newOption);
          if (props.field.multiple) {
            emit('update:modelValue', [...props.modelValue as Array<unknown>, newOption.value]);
          }
          else {
            emit('update:modelValue', newOption.value);
          }
        }
        catch (err) {
          toastAlerts.error(`Error adding option: ${(err as any).message}`);
        }
      }
    },
  });
};
</script>
